import React from "react";
import cns from "classnames";
import {
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableHead,
  Tooltip
} from "@material-ui/core";
import { Done, Error } from "@material-ui/icons";
import { ThemeType } from "@theme";
import locale from "@bonliva-crm/locale";
import {
  ContactListItemFragment,
  SMSDeliveryStatus,
  MassContactActions_SMSResultFragment as SMSResult,
  MassContactActions_SMSComponent
} from "@graphql";
import { Spinner, PlainLink } from "@components";
import { DropdownCheckbox, DropdownCheckboxOption } from "@components/DropdownCheckbox";
import { TableRow } from "@components/TableRow";
import { makeStyles } from "@material-ui/core/styles";

type Item = ContactListItemFragment & { Unavailable?: boolean };

export type ContactListColumn =
  | "CellPhone"
  | "Email"
  | "ResponsiblePerson"
  | "Delete"
  | "Role"
  | "Title";

type Props = {
  className?: string;
  columns: ContactListColumn[];
  items: Item[];
  loading?: boolean;
  onSelect: (item: Item) => any;
  dropdownCheckboxOptions?: DropdownCheckboxOption[];
  onSelectAll: () => any;
  isAllSelected: boolean;
  isSelectAllDisabled: boolean;
  smsResult?: SMSResult["result"];
  selectedIds?: string[];
};

export function ContactList(props: Props) {
  const classes = useStyles({});
  const { columns, items, loading, selectedIds, smsResult = [] } = props;

  const has = (column: ContactListColumn) => columns.includes(column);

  const totalColumns =
    7 -
    (has("CellPhone") ? 0 : 2) -
    (has("Email") ? 0 : 2) -
    (has("ResponsiblePerson") ? 0 : 1);

  return (
    <div className={cns(classes.root, props.className)} data-test="ContactList">
      <Table>
        <TableHead>
          <TableRow className={classes.row}>
            <TableCell padding="checkbox">
              {props.dropdownCheckboxOptions.length > 0 && (
                <DropdownCheckbox
                  disabled={props.isSelectAllDisabled}
                  CheckboxProps={{
                    checked: props.isAllSelected,
                    onChange: props.onSelectAll
                  }}
                  options={props.dropdownCheckboxOptions}
                />
              )}

              {props.dropdownCheckboxOptions.length === 0 && (
                <Checkbox
                  disabled={props.isSelectAllDisabled}
                  checked={props.isAllSelected}
                  onChange={props.onSelectAll}
                />
              )}
            </TableCell>
            <TableCell>{locale.Name}</TableCell>

            {has("CellPhone") && <TableCell />}
            {has("CellPhone") && <TableCell>{locale["CellPhone number"]}</TableCell>}

            {has("Email") && <TableCell />}
            {has("Email") && <TableCell>{locale.Email}</TableCell>}

            {has("Role") && <TableCell>Role</TableCell>}
            {has("Title") && <TableCell>Title</TableCell>}

            {has("ResponsiblePerson") && (
              <TableCell>{locale["Responsible BA"]}</TableCell>
            )}
          </TableRow>
        </TableHead>

        {loading && (
          <TableBody>
            <TableRow>
              <TableCell colSpan={totalColumns} className={classes.loader}>
                <Spinner style={{ display: "block" }} />
              </TableCell>
            </TableRow>
          </TableBody>
        )}

        {!loading && (
          <TableBody>
            {items.length ? (
              items.map((item, index) => {
                const itemResult = smsResult.find((x) => x.ConsultantId === item._id);

                const status = itemResult ? (
                  <MassContactActions_SMSComponent
                    variables={{ messageId: itemResult.SMSMessage._id }}
                  >
                    {({ data }) => (
                      <div className={classes.smsStatus}>
                        {smsStatusToIcon(
                          data
                            ? data.smsMessageUpdated.Status
                            : itemResult.SMSMessage.Status
                        )}
                      </div>
                    )}
                  </MassContactActions_SMSComponent>
                ) : null;

                if (!item._id) {
                  item._id = `contact${index}`;
                }

                return (
                  <TableRow
                    key={item._id}
                    data-test="ContactList-item"
                    className={classes.row}
                    disabled={item.Unavailable}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedIds.includes(item._id)}
                        onChange={() => props.onSelect(item)}
                        disabled={item.Unavailable}
                        style={{ color: item.Unavailable ? "gray" : undefined }}
                      />
                    </TableCell>

                    <TableCell>{item.Name}</TableCell>

                    {has("CellPhone") && (
                      <TableCell>
                        {item.CellPhone && !item.CellPhoneValid && (
                          <Tooltip title={locale["Invalid cellphone"]}>
                            <Error className={classes.warning} color="primary" />
                          </Tooltip>
                        )}
                        {status}
                      </TableCell>
                    )}

                    {has("CellPhone") && <TableCell>{item.CellPhone}</TableCell>}

                    {has("Email") && (
                      <>
                        <TableCell>
                          {item.Email && !item.EmailValid && (
                            <Tooltip title={locale["Invalid email address"]}>
                              <Error className={classes.warning} color="primary" />
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell>
                          <PlainLink>{item.Email} </PlainLink>
                        </TableCell>
                      </>
                    )}

                    {has("ResponsiblePerson") && (
                      <TableCell>
                        {item.ResponsiblePerson ? item.ResponsiblePerson.Name : ""}
                      </TableCell>
                    )}

                    {has("Role") && (
                      <TableCell>
                        {item.Roles.map((role, idx) => (
                          <div key={idx}>{role}</div>
                        ))}
                      </TableCell>
                    )}

                    {has("Title") && <TableCell>{item.Title}</TableCell>}
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  {locale.NoContacts}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        )}
      </Table>
    </div>
  );
}

ContactList.defaultProps = {
  items: [],
  selectedIds: []
};

const useStyles = makeStyles((theme: ThemeType) => ({
  root: {
    userSelect: "none"
  },
  item: {},
  loader: {
    height: 100,
    textAlign: "center"
  },
  warning: {},
  row: {
    "& td,th": {
      padding: 5
    }
  },
  smsStatus: {
    float: "right"
  }
}));

const smsStatusToIcon = (status: SMSDeliveryStatus) => {
  switch (status) {
    case SMSDeliveryStatus.Pending:
      return <Spinner size={20} />;

    case SMSDeliveryStatus.Delivered:
      return <Done color="secondary" />;

    case SMSDeliveryStatus.Error:
    case SMSDeliveryStatus.Expired:
    case SMSDeliveryStatus.Rejected:
    case SMSDeliveryStatus.Unknown:
      return (
        <Tooltip title={status}>
          <Error color="error" />
        </Tooltip>
      );
  }

  return null;
};
